/**
 * Generic document upload hooks for borrower file management
 * Provides reusable hooks for appending files to different document types
 */

import { useSelector, useDispatch } from "react-redux";
import { useMemo, useCallback } from "react";
import {
  setBalance,
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setBankDoc,
  setSalaryProofDoc,
  setFinancialStatementDoc,
  setInvestmentMandateDoc,
  setFundManagerLicenseDoc,
  UploadedFile,
  setSupportingDocumentsWithSuppliersDoc,
  setCertificateOfIncorporationDoc,
  setCompanyConstitutionDoc,
} from "../features/merchant/doc";

import { RootState } from "@funded-here-interface/investor-onboarding/src/store";

/**
 * Configuration for a document type
 * Uses unwrapped payload pattern for Redux actions
 */
interface DocumentConfig<TKey extends keyof RootState["bordoc"]> {
  stateKey: TKey;
  actionCreator: (payload: UploadedFile[]) => any;
}

/**
 * Generic hook factory for document array management
 * Creates hooks that manage appending files to document arrays in Redux
 * Uses unwrapped payload pattern for efficient Redux updates
 *
 * @template TKey - The key in the bordoc state slice
 * @param config - Configuration object with state key and action creator
 * @returns Hook with document array and append function
 */
const createDocumentHook = <TKey extends keyof RootState["bordoc"]>(
  config: DocumentConfig<TKey>
) => {
  return () => {
    const dispatch = useDispatch();
    const documents = useSelector(
      (state: RootState) => state.bordoc[config.stateKey]
    ) as UploadedFile[] | null;

    const appendDocument = useCallback(
      (newValue: UploadedFile, original: UploadedFile[] | null) => {
        const updatedArray = original !== null ? [...original, newValue] : [newValue];
        // Use unwrapped payload pattern - pass array directly
        dispatch(config.actionCreator(updatedArray));
      },
      [dispatch]
    );

    // Return object with dynamic key matching the field name for backward compatibility
    return useMemo(
      () => ({
        [config.stateKey]: documents,
        [`appendTo${config.stateKey.charAt(0).toUpperCase() + config.stateKey.slice(1)}`]: appendDocument,
      }),
      [documents, appendDocument]
    ) as any; // Type assertion needed due to dynamic keys
  };
};

/**
 * Hook for balance sheet documents
 */
export const useAppendToBalance = createDocumentHook({
  stateKey: "balance",
  actionCreator: setBalance,
});

/**
 * Hook for shareholder documents
 */
export const useShareArray = createDocumentHook({
  stateKey: "share",
  actionCreator: setShareDoc,
});

/**
 * Hook for business profile documents
 */
export const useBusiness = createDocumentHook({
  stateKey: "business",
  actionCreator: setBusinessDoc,
});

/**
 * Hook for proof of address documents
 */
export const useProofArray = createDocumentHook({
  stateKey: "proof",
  actionCreator: setProofDoc,
});

/**
 * Hook for bank statement documents
 */
export const useBankArray = createDocumentHook({
  stateKey: "bank",
  actionCreator: setBankDoc,
});

/**
 * Hook for salary proof documents
 */
export const useSalaryProofArray = createDocumentHook({
  stateKey: "salaryProof",
  actionCreator: setSalaryProofDoc,
});

/**
 * Hook for financial statement documents
 */
export const useFinancialStatementArray = createDocumentHook({
  stateKey: "financialStatement",
  actionCreator: setFinancialStatementDoc,
});

/**
 * Hook for investment mandate documents
 */
export const useInvestmentMandateArray = createDocumentHook({
  stateKey: "investmentMandate",
  actionCreator: setInvestmentMandateDoc,
});

/**
 * Hook for fund manager license documents
 */
export const useFundManagerLicenseArray = createDocumentHook({
  stateKey: "fundManagerLicense",
  actionCreator: setFundManagerLicenseDoc,
});

/**
 * Hook for certificate of incorporation documents
 */
export const useCerificateOfIncorporation = createDocumentHook({
  stateKey: "certificateOfIncorporation",
  actionCreator: setCertificateOfIncorporationDoc,
});

/**
 * Hook for supporting documents with suppliers
 */
export const useSupportingDocumentsWithSuppliers = createDocumentHook({
  stateKey: "supportingDocumentsWithSuppliers",
  actionCreator: setSupportingDocumentsWithSuppliersDoc,
});

/**
 * Hook for company constitution documents
 */
export const useCompanyConstitutionArray = createDocumentHook({
  stateKey: "companyConstitution",
  actionCreator: setCompanyConstitutionDoc,
});
